import moment from 'moment'
import { prop, head, last, difference, map, pipe } from 'ramda'
import { useLogger } from '@booksy-com/vue-tools'

import {
  FORMAT,
  BOOKING_TYPE_BREAK,
  BOOKING_TYPE_RESERVATION,
  BOOKING_TYPE_NOT_WORKING,
  BOOKING_TYPE_TIMEOFF,
} from '@/lib/consts.ts'

import eventProxy from '@/proxies/event.js'

import {
  sortEvents,
} from '@/components/calendar/functions.js'

import {
  mapAppointmentStaffer,
} from '@/components/appointment/functions.js'

const logger = useLogger('calendar-service-utils')

export function saveMonthData (data, store, root) {
  const _eventProxy = eventProxy(root)

  const annotateDays = (day) => {
    return {
      bookings: map(pipe(checkAndNormalize, _eventProxy), day.bookings),
      counter: day.counter,
      business_time_off: day.business.time_off,
      resource_time_off: day.resources.time_offs,

    }
  }

  store.monthCalendar = map(annotateDays, data)
}

export function checkAndNormalize (event) {
  if (event.type === BOOKING_TYPE_RESERVATION) {
    return normalizeReservation(event)
  }

  return event
}

export function mergeCalendarData (data, store, root) {
  store.resources = data.resources.map(mapAppointmentStaffer)

  store.resources.forEach((r, index) => { r.$$order = index })

  store.resourcesMap = store.resources.reduce((rmap, resource) => {
    rmap[resource.id] = resource
    return rmap
  }, {})

  const annotateEventResources = (event) => {
    event.resources = event.resources
      .map(res => store.resourcesMap[String(res.id)])
      .filter(res => res) // filter `undefined` res. due to permissions

    return event
  }

  const _eventProxy = eventProxy(root)

  Object.assign(store.agenda, data.agenda)
  Object.assign(store.bookings, map(pipe(_eventProxy, annotateEventResources), data.bookings))
  Object.assign(store.reservations, map(pipe(normalizeReservation, _eventProxy, annotateEventResources), data.reservations))
  Object.assign(store.open_hours_customizations, data.open_hours_customizations)

  // for convenience, get dates list from working hours
  Object.keys(data.resources[0].working_hours).map(day => {
    store.days[day] = data.resources.map(resource => {
      const events = [].concat(
        (resource.bookings[day] || []).map(b => store.bookings[String(b)]),
        (resource.reservations[day] || []).map(d => store.reservations[String(d)]),
        resourceTimeOffs(resource.time_offs[day], _eventProxy),
        nonWorkingFromWorking(resource.working_hours[day], day, resource.id),
        breaksFromWorking(resource.working_hours[day], day, resource.id, _eventProxy)
      ).sort((a, b) => sortEvents(a, b))

      return {
        id: resource.id,
        type: resource.type,
        events,
        // remove working_hours when resource has time off
        working_hours: (resource.time_offs[day] || []).filter(({ all_day }) => all_day).length ? [] : resource.working_hours[day],
      }
    })
  })

  logger.debug('merged calendar', store)
}

export function resourceTimeOffs (timeOffs = [], _eventProxy) {
  return (timeOffs || []).map(d => {
    return _eventProxy(normalizeResourceTimeOff(d))
  })
}

export function breaksFromWorking (resourceWorkingHours, day, resourceId, proxy) {
  if (!resourceWorkingHours) return []

  const breaks = resourceWorkingHours
    .reduce((breaks, h, index, all) => {
      if (index === all.length - 1) return breaks

      breaks.push({
        booked_from: moment(day + 'T' + h.hour_till),
        booked_till: moment(day + 'T' + all[index + 1].hour_from),
        type: BOOKING_TYPE_BREAK,
        id: `break_${resourceId}_${h.hour_from}_${h.hour_till}`,
      })
      return breaks
    }, [])
    .map(b => proxy(b))

  return breaks
}

export function nonWorkingFromWorking (resourceWorkingHours, day, resourceId) {
  if (!(resourceWorkingHours || []).length) {
    return nonWorkingEvent(
      moment(day + 'T00:00', FORMAT),
      moment(day + 'T23:59', FORMAT),
      resourceId,
      0
    )
  }

  const workingWithoutBreaks = resourceWorkingHours.length > 1
    ? [{
      hour_from: prop('hour_from', head(resourceWorkingHours)),
      hour_till: prop('hour_till', last(resourceWorkingHours)),
    }]
    : [head(resourceWorkingHours)]

  let result = workingWithoutBreaks
    .map(h => {
      return {
        from: moment(day + 'T' + h.hour_from, FORMAT),
        till: moment(day + 'T' + h.hour_till, FORMAT),
      }
    })
    .sort((a, b) => sortEvents(a, b))
    .reduce((nonWorking, item, index, all) => {
      if (index === 0) {
        nonWorking.push(
          nonWorkingEvent(
            moment(day + 'T00:00', FORMAT),
            item.from,
            resourceId,
            index
          )
        )
      }

      nonWorking.push(
        nonWorkingEvent(
          item.till,
          (index < all.length - 1) ? all[index + 1].from : moment(day + 'T23:59', FORMAT),
          resourceId,
          index
        )
      )

      return nonWorking
    }, [])

  return result
}

export function nonWorkingEvent (from, till, resourceId, index) {
  // we need unique id for calendar-grid v-for
  return {
    isNonworking: true,
    from,
    till,
    id: `_nw_${resourceId}-${index}-${from}`,
    type: BOOKING_TYPE_NOT_WORKING,
  }
}

export function normalizeTime (prefix, event) {
  event.booked_from = event[`${prefix}_from`]
  delete event[`${prefix}_from`]

  event.booked_till = event[`${prefix}_till`]
  delete event[`${prefix}_till`]

  return event
}

export function normalizeReservation (event) {
  return normalizeTime('reserved', event)
}

function normalizeResourceTimeOff (event) {
  event.booked_from = `${event.start_date}T${event.hour_from}`
  event.booked_till = `${event.start_date}T${event.hour_till}`
  event.type = BOOKING_TYPE_TIMEOFF
  return event
}

// bookings: {},
// reservations: {},
// time_offs: {},
// resources: [],
// days: {},
// invalidDays: {},
// version: 0,
// open_hours_customizations: {},
// monthCalendar: {}

export function clearAllDays (store) {
  logger.debug('clearAllDays')
  store.bookings = {}
  store.reservations = {}
  store.time_off = {}
  store.open_hours_customizations = {}
  store.days = {}
  store.invalidDays = {}
  store.resources.forEach(r => {
    r.bookings = {}
    r.resevrations = {}
  })
}

export function clearMonth (store) {
  logger.debug('clearMonth')
  store.monthCalendar = {}
}

export function clearDays (store, days) {
  const diff = difference(Object.keys(store.days), days)
  logger.debug('clearDays', diff)
  diff.forEach(d => {
    delete store.days[d]
    delete store.invalidDays[d]
    store.resources.forEach(r => {
      r.bookings && delete r.bookings[d]
      r.reservations && delete r.resevrations[d]
    })
  })
}
